import ClearIcon from "@mui/icons-material/Clear"
import ExpandMore from "@mui/icons-material/ExpandMore"
import FilterListIcon from "@mui/icons-material/FilterList"
import {
  Button,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import { InputField } from "src/components/common/inputfield"

import { FilterSortSelection } from "src/components/core/filterSortSelector/FilterSortSelector"
import { create } from "src/helpers/bem"
import { Category } from "src/states/shop/category"
import { FilterSorting, mergeFilter, useFilter } from "src/states/shop/filter"
import { mergeProducts, useProducts } from "src/states/shop/products"
import { TranslationMessages } from "src/translations"

import { searchProducts } from "./shopCategoryFilter"
import { ShopOverviewCategorySheet } from "./ShopOverviewCategorySheet"
import styles from "./ShopOverviewProductsHeader.module.scss"

const bem = create(styles, "ShopOverviewProductsHeader")

export type ShopOverviewProductsHeaderProps = {
  messages: TranslationMessages
}

export const productsSortingOptions = [
  "default",
  "favorite",
  "alphabeticUp",
  "alphabeticDown",
  "priceUp",
  "priceDown",
] as FilterSorting[]

export const ShopOverviewProductsHeader: FC<
  ShopOverviewProductsHeaderProps
> = ({ messages }) => {
  const m = messages.pages.shop.overview.header
  const { products, loading: loadingProducts } = useProducts()
  const { filterBy, search, sortBy } = useFilter()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("xl"))
  const [openCategories, setOpenCategories] = useState(false)

  const isAllProductsCategory = (category: Category) =>
    category.categoryType === "category" && category.children === undefined

  const onFilter = useCallback(async () => {
    if (products.length === 0 || loadingProducts) return

    let result = [...products]

    if (
      filterBy?.category?.id &&
      !isAllProductsCategory(filterBy.category as Category)
    ) {
      result = result.filter((p) =>
        [p.categoryId, p.parentCategoryId].includes(filterBy.category?.id),
      )
    }

    result = result.filter(
      (p) =>
        p.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.note?.toLowerCase().includes(search.toLowerCase()),
    )

    switch (sortBy) {
      case "alphabeticUp":
        result.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
        break
      case "alphabeticDown":
        result.sort((a, b) => (b.name ?? "").localeCompare(a.name ?? ""))
        break
      case "priceUp":
        result.sort((a, b) => (a.price ?? 0) - (b.price ?? 0))
        break
      case "priceDown":
        result.sort((a, b) => (b.price ?? 0) - (a.price ?? 0))
        break
      case "favorite":
        result.sort((a, _) => (a.favorite ? -1 : 1))
        break
    }

    mergeProducts({
      filteredProducts: result.filter((p) => p.isValid),
    })
  }, [filterBy, products, search, sortBy, loadingProducts])

  // onFilter is intentionally excluded from deps: products changes are
  // handled by loadProducts in ShopOverview, the filter effect should only
  // run when the user changes filter criteria (filterBy, sortBy, search).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onFilter()
  }, [filterBy, sortBy, search])

  const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    searchProducts(e?.target?.value ?? "")
  }

  const handleChangeSorting = (value: string) => {
    const sortingValue = value as FilterSorting
    mergeFilter({
      sortBy: sortingValue,
    })
  }

  const sortingItems = useMemo(
    () =>
      productsSortingOptions.map((key) => ({
        id: key,
        label: m.sorting.items?.[key],
      })),
    [m.sorting.items],
  )

  const renderCategoryButton = () => (
    <>
      <Button
        aria-expanded={openCategories}
        aria-haspopup="dialog"
        className={bem("category")}
        endIcon={<ExpandMore />}
        startIcon={<FilterListIcon />}
        variant="outlined"
        onClick={() => setOpenCategories(true)}
      >
        <span className={bem("category__label")}>
          {m.category.label}: {filterBy?.category?.name ?? ""}
        </span>
      </Button>
      <ShopOverviewCategorySheet
        messages={messages}
        open={openCategories}
        onClose={() => setOpenCategories(false)}
      />
    </>
  )

  const searchField = (
    <InputField
      label={m.search.input}
      name="search"
      sizeVariant="responsive"
      type="text"
      value={search}
      classes={{
        root: bem("input"),
      }}
      InputProps={{
        endAdornment: search ? (
          <InputAdornment position="end">
            <IconButton
              aria-label={m.search.clear}
              className={bem("search__clear")}
              size="small"
              title={m.search.clear}
              onClick={() => searchProducts("")}
            >
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
      onChange={handleChangeSearch}
    />
  )

  const sortField = (
    <FilterSortSelection
      className={bem("filter")}
      options={sortingItems}
      value={sortBy}
      onChange={handleChangeSorting}
    />
  )

  // Small screens: search on top, category and sorting side by side below
  if (isMobile) {
    return (
      <div className={bem(undefined, { mobile: true })}>
        <div className={bem("search")}>{searchField}</div>
        <div className={bem("actions")}>
          {renderCategoryButton()}
          {sortField}
        </div>
      </div>
    )
  }

  return (
    <div className={bem()}>
      <div className={bem("toolbar")}>
        <div className={bem("search")}>{searchField}</div>
        {sortField}
      </div>
    </div>
  )
}
